import type { SearchValuesArrayObject } from "types";

type NumberSearchFieldSource = {
  searchFields: Array<{
    attributes?: {
      alias_name?: string;
      type?: string;
    };
  }>;
};
export function priceFormat(price: string | number): string {
  const numericPrice =
    typeof price === "string" ? Number.parseFloat(price) : price;

  if (!Number.isFinite(numericPrice)) {
    return "";
  }
  const [integerPart, decimalPart] = numericPrice.toFixed(2).split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${formattedInteger},${decimalPart} €`;
}
export function normalizeNumberStringForUrl(raw: unknown): string {
  if (raw === null || raw === undefined) return "";

  let value = String(raw).trim();
  if (!value) return value;

  const isNegative = value.startsWith("-");

  if (value[0] === "+" || value[0] === "-") {
    value = value.slice(1);
  }

  value = value.replace(/[^0-9.,'\s]/g, "");

  const lastDot = value.lastIndexOf(".");
  const lastComma = value.lastIndexOf(",");

  const buildNumber = (
    integerPart: string,
    decimalPart: string | null,
  ): string => {
    const cleanInteger = integerPart.replace(/[.,\s']/g, "");
    const cleanDecimal = decimalPart ? decimalPart.replace(/[^0-9]/g, "") : "";

    const normalized = cleanDecimal
      ? `${cleanInteger}.${cleanDecimal}`
      : cleanInteger;

    return `${isNegative ? "-" : ""}${normalized}`;
  };

  if (lastDot !== -1 && lastComma !== -1) {
    if (lastDot > lastComma) {
      const parts = value.split(".");
      const decimalPart = parts.pop() ?? "";

      return buildNumber(parts.join("."), decimalPart);
    }

    const parts = value.split(",");
    const decimalPart = parts.pop() ?? "";

    return buildNumber(parts.join(","), decimalPart);
  }

  if (lastComma !== -1) {
    const parts = value.split(",");
    const decimalPart = parts.pop() ?? "";

    return buildNumber(parts.join(","), decimalPart);
  }

  if (lastDot !== -1) {
    const parts = value.split(".");
    const decimalPart = parts.pop() ?? "";

    return buildNumber(parts.join("."), decimalPart);
  }

  return buildNumber(value, null);
}

export function normalizeNumberString(
  raw: unknown,
  precision: number | string | null | undefined,
  thousandSeparator?: string | null,
): string {
  if (raw === null || raw === undefined) return "";

  let value = String(raw).trim();
  if (!value) return value;

  const isNegative = value.startsWith("-");

  if (value[0] === "+" || value[0] === "-") {
    value = value.slice(1);
  }

  value = value.replace(/[^0-9.,'\s]/g, "");

  const parsedPrecision =
    precision === null || precision === undefined || precision === ""
      ? null
      : Number(precision);

  const normalizedPrecision =
    parsedPrecision !== null &&
    !Number.isNaN(parsedPrecision) &&
    parsedPrecision >= 0
      ? parsedPrecision
      : null;

  const digitsOnly = (input: string): string => input.replace(/[^0-9]/g, "");

  if (normalizedPrecision === 0) {
    const normalized = digitsOnly(value);
    return `${isNegative ? "-" : ""}${normalized}`;
  }

  if (normalizedPrecision === null) {
    return normalizeNumberStringForUrl(`${isNegative ? "-" : ""}${value}`);
  }

  const normalizedThousandSeparator =
    thousandSeparator === "." || thousandSeparator === ","
      ? thousandSeparator
      : null;

  if (!normalizedThousandSeparator) {
    return normalizeNumberStringForUrl(`${isNegative ? "-" : ""}${value}`);
  }

  const decimalSeparator: "." | "," =
    normalizedThousandSeparator === "." ? "," : ".";

  const decimalIndex = value.lastIndexOf(decimalSeparator);

  let integerPart = value;
  let decimalPart = "";

  if (decimalIndex !== -1) {
    integerPart = value.slice(0, decimalIndex);
    decimalPart = value.slice(decimalIndex + 1);
  }

  const cleanInteger = integerPart.replace(/[.,\s']/g, "");
  const cleanDecimal = digitsOnly(decimalPart);

  const normalized = cleanDecimal
    ? `${cleanInteger || "0"}.${cleanDecimal}`
    : cleanInteger || "0";

  return `${isNegative ? "-" : ""}${normalized}`;
}

export function getSanitizedNumber(
  raw: unknown,
  precision: number | string | null | undefined,
  separator?: string | null,
): string {
  const originalValue = String(raw);

  const sanitizedValue = originalValue.replace(/[^0-9,.-]/g, "").trim();

  if (!sanitizedValue) {
    return "";
  }

  const isNegative = sanitizedValue.startsWith("-");
  const unsignedValue = isNegative ? sanitizedValue.slice(1) : sanitizedValue;

  let normalizedPrecision: number | null;

  if (precision === null || precision === undefined || precision === "") {
    normalizedPrecision = null;
  } else {
    const parsedPrecision = Number(precision);

    normalizedPrecision =
      !Number.isNaN(parsedPrecision) && parsedPrecision >= 0
        ? parsedPrecision
        : null;
  }

  let numericValue: number;

  if (normalizedPrecision === null) {
    const digits = unsignedValue.replace(/[^0-9]/g, "");

    if (!digits) {
      return originalValue;
    }

    numericValue = Number(digits);

    if (isNegative) {
      numericValue = -numericValue;
    }
  } else {
    const lastComma = unsignedValue.lastIndexOf(",");
    const lastDot = unsignedValue.lastIndexOf(".");

    let decimalIndex = -1;

    if (lastComma === -1 && lastDot === -1) {
      decimalIndex = -1;
    } else if (lastComma === -1) {
      decimalIndex = lastDot;
    } else if (lastDot === -1) {
      decimalIndex = lastComma;
    } else {
      decimalIndex = Math.max(lastComma, lastDot);
    }

    let integerPart: string;
    let decimalPart: string;

    if (decimalIndex === -1) {
      integerPart = unsignedValue.replace(/[^0-9]/g, "");
      decimalPart = "";
    } else {
      integerPart = unsignedValue.slice(0, decimalIndex).replace(/[^0-9]/g, "");

      decimalPart = unsignedValue
        .slice(decimalIndex + 1)
        .replace(/[^0-9]/g, "");
    }

    if (!integerPart && !decimalPart) {
      return originalValue;
    }

    let numericString = integerPart || "0";

    if (decimalPart) {
      numericString += `.${decimalPart}`;
    }

    numericValue = Number(numericString);

    if (isNegative) {
      numericValue = -numericValue;
    }

    if (Number.isNaN(numericValue)) {
      return originalValue;
    }
  }

  const sign = numericValue < 0 ? "-" : "";
  const absoluteValue = Math.abs(numericValue);

  let integerPart: string;
  let decimalPart: string | null = null;

  if (normalizedPrecision === null) {
    integerPart = String(Math.trunc(absoluteValue));
  } else {
    const fixedValue = absoluteValue.toFixed(normalizedPrecision);
    const parts = fixedValue.split(".");

    integerPart = parts[0];
    decimalPart = parts[1] ?? "";
  }

  if (separator === "," || separator === ".") {
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  }

  if (
    normalizedPrecision === null ||
    normalizedPrecision === 0 ||
    decimalPart === null
  ) {
    return `${sign}${integerPart}`;
  }

  return `${sign}${integerPart},${decimalPart}`;
}

export function parsePriceToNumber(value: unknown): number {
  if (value === null || value === undefined) {
    return Number.NaN;
  }

  if (typeof value === "number") {
    return value;
  }

  const sanitizedValue = String(value)
    .replace(/\u00a0/g, " ")
    .replace(/\s/g, "")
    .replace(/[^0-9,.-]/g, "")
    .replace(/,/g, ".");

  const parsedValue = Number(sanitizedValue);

  return Number.isFinite(parsedValue) ? parsedValue : Number.NaN;
}

export function tariffPrice(currentPrice: string | number): string {
  const priceParts = Number.parseFloat(String(currentPrice))
    .toFixed(2)
    .split(".");

  const formattedPrice = `${priceParts[0].replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ".",
  )},${priceParts[1]}`;

  return formattedPrice === "0,00" ? "Nicht verfügbar" : `${formattedPrice} €`;
}

export function getPriceChangeState(oldPrice: unknown, newPrice: unknown) {
  const oldNum = parsePriceToNumber(oldPrice);
  const newNum = parsePriceToNumber(newPrice);

  const hasChanged =
    Number.isFinite(oldNum) && Number.isFinite(newNum) && oldNum !== newNum;

  return {
    oldNum,
    newNum,
    hasChanged,
    increased: hasChanged && newNum > oldNum,
    decreased: hasChanged && newNum < oldNum,
    deltaSymbol: hasChanged ? (newNum > oldNum ? "↑" : "↓") : "",
  };
}

export function withDotDecimalsForNumberFields(
  values: SearchValuesArrayObject[],
  searchFieldsSource: NumberSearchFieldSource,
): SearchValuesArrayObject[] {
  if (!Array.isArray(values)) {
    return values;
  }

  return values.map((item) => {
    const field = searchFieldsSource.searchFields.find(
      (candidate) => candidate.attributes?.alias_name === item?.type,
    );

    if (
      field?.attributes?.type === "number" &&
      typeof item?.value === "string"
    ) {
      return {
        ...item,
        value: normalizeNumberStringForUrl(item.value),
      };
    }

    return item;
  });
}
